/**
 * Simple rate limiter using sliding window algorithm.
 * Tracks message timestamps and checks if rate exceeds configured threshold.
 */
export class RateLimiter {
  private timestamps: number[] = [];
  private violationCount = 0;
  private reportThrottle: number;

  /**
   * @param maxMessages Maximum number of messages allowed in the time window
   * @param windowMs Time window in milliseconds
   * @param reportThrottle Report to Sentry every Nth violation (default: 10)
   */
  constructor(
    private maxMessages: number,
    private windowMs: number,
    reportThrottle = 10
  ) {
    this.reportThrottle = reportThrottle;
  }

  /**
   * Check if the rate limit is currently being exceeded.
   * Automatically cleans up old timestamps outside the window.
   * @returns true if rate limit is exceeded, false otherwise
   */
  isExceeding(): boolean {
    const now = Date.now();
    // Remove timestamps outside the sliding window
    this.timestamps = this.timestamps.filter((t) => now - t < this.windowMs);
    return this.timestamps.length >= this.maxMessages;
  }

  /**
   * Record a new message timestamp.
   * Should be called after successfully sending a message.
   */
  recordMessage(): void {
    this.timestamps.push(Date.now());
  }

  /**
   * Check if a Sentry report should be sent for this violation.
   * Reports only every Nth violation to avoid spamming Sentry.
   * @returns true if this violation should be reported to Sentry
   */
  shouldReportToSentry(): boolean {
    this.violationCount++;
    return this.violationCount % this.reportThrottle === 1; // Report 1st, 11th, 21st, etc.
  }

  /**
   * Get current rate statistics for debugging/reporting.
   */
  getStats(): {
    currentCount: number;
    maxMessages: number;
    windowMs: number;
    violationCount: number;
  } {
    const now = Date.now();
    this.timestamps = this.timestamps.filter((t) => now - t < this.windowMs);
    return {
      currentCount: this.timestamps.length,
      maxMessages: this.maxMessages,
      windowMs: this.windowMs,
      violationCount: this.violationCount
    };
  }

  /**
   * Reset violation counter (useful for testing)
   */
  reset(): void {
    this.timestamps = [];
    this.violationCount = 0;
  }
}
